import { Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Text, Flex, Link, Skeleton } from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import { useHasLoaded } from 'hooks/useHasLoaded';
import { useParams } from 'react-router-dom';
import { GlobalContext } from '../../providers/Global';
import { ModalType, SCREEN_LG, SCREEN_MD, SCREEN_SM } from '../../utils/constants';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as ExternalIcon } from 'assets/external-icon.svg';
import { ReactComponent as HederaLogo } from 'assets/hedera-logo.svg';
import TransactionList from './components/TransactionList';
import TokenIcon from 'components/TokenIcon';
import copyToClipboard from 'copy-to-clipboard';
import { useToast } from 'hooks/useToast';
import { useMediaQuery } from 'react-responsive';
import { MobileMenu } from './components/MobileMenu';
import { ApplicationRoutes } from 'utils/routes';
import { formatTokenBalance } from 'utils/tokens';
import AnimatedNumber from 'components/AnimatedNumber';
import { getExplorerUrl } from 'utils/explorerUrl';
import { getNetworkDisplayName } from 'utils/networkConfigStore';

const AccountPage = () => {
  const { showToast } = useToast();
  const { currentAccount, connectedNetwork, openModal, setSelectedToken } = useContext(GlobalContext);
  const { walletId, multisigAccountId } = useParams();

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });
  const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });
  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const balanceCanAnimate = useHasLoaded(currentAccount.balances);

  const topTokens = useMemo(() => {
    const all = currentAccount.tokens || [];
    return [...all]
      .sort((a, b) => {
        const balA = a.balance / Math.pow(10, a.decimals);
        const balB = b.balance / Math.pow(10, b.decimals);
        return balB - balA;
      })
      .slice(0, 2);
  }, [currentAccount.tokens]);

  return (
    <Flex flex={1} flexDirection="column">
      <MobileMenu
        pageName='Dashboard'
        backUrl='/'
      />
      <Flex
        flex={1}
        flexDirection="column"
        gap={4}
      >
        <Flex
          gap={4}
          direction={isScreenMedium ? 'column' : 'row'}
        >
          <Box
            p={6}
            pt={isScreenSmall ? 4 : 6}
            flex={1}
            bg="white"
            borderRadius={16}
            boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
          >
            <Text
              size="lg"
              fontWeight={300}
              color="blackAlpha.500"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              Account Details
              {
                isScreenLarge && (
                  <ChakraLink
                    as={RouterLink}
                    to={`${ApplicationRoutes.Dashboard}/${walletId}/settings/${multisigAccountId}`}
                    cursor="pointer"
                    fontSize="sm"
                    fontWeight="500"
                    letterSpacing="0.5px"
                    color="blackAlpha.900"
                    textDecoration="none !important"
                  >
                    View
                  </ChakraLink>
                )
              }
            </Text>
            <Box
              p={4}
              mt={isScreenSmall ? 2 : 6}
              mb={4}
              bg="primary"
              borderRadius={8}
            >
              <Text size="sm">Wallet address</Text>
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Text size="lg" variant="bodyBold">
                  {multisigAccountId}
                </Text>
                <CopyIcon
                  cursor="pointer"
                  onClick={() => {
                    copyToClipboard(multisigAccountId || '');
                    showToast('Copied to clipboard', 'success', undefined, 'top');
                  }}
                />
                {getExplorerUrl(connectedNetwork!, 'account', multisigAccountId!) && (
                  <Link
                    href={getExplorerUrl(connectedNetwork!, 'account', multisigAccountId!)!}
                    cursor="pointer"
                    isExternal
                  >
                    <ExternalIcon />
                  </Link>
                )}
              </Box>
            </Box>
            <Flex p={4} bg="primary" justifyContent="space-between" borderRadius={8}>
              <Box>
                <Text size="sm">Threshold</Text>
                <Skeleton isLoaded={!!currentAccount?.threshold} borderRadius={6} minW="80px" minH="24px" mt={1}>
                  <Text size="lg" variant="bodyBold">
                    {currentAccount?.threshold} out of {currentAccount?.keys?.length}
                  </Text>
                </Skeleton>
              </Box>
              <Box>
                <Text size="sm">Network</Text>
                <Skeleton isLoaded={!!currentAccount?.network} borderRadius={6} minW="80px" minH="24px" mt={1}>
                  <Text size="lg" variant="bodyBold" textTransform="capitalize">
                    {getNetworkDisplayName(currentAccount?.network || '')}
                  </Text>
                </Skeleton>
              </Box>
            </Flex>
          </Box>
          <Box
            p={6}
            pt={isScreenSmall ? 4 : 6}
            flex={1}
            bg="white"
            boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
            borderRadius={16}
          >
            <Text
              size="lg"
              fontWeight={300}
              color="blackAlpha.500"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              Asset Balance
              <ChakraLink
                as={RouterLink}
                to={`${ApplicationRoutes.Dashboard}/${walletId}/assets/${multisigAccountId}`}
                cursor="pointer"
                fontSize="sm"
                fontWeight="500"
                letterSpacing="0.5px"
                color="blackAlpha.900"
                textDecoration="none !important"
              >
                View All
              </ChakraLink>
            </Text>

            <Flex
              p={4}
              mt={isScreenSmall ? 2 : 6}
              bg="primary"
              borderRadius={8}
              justifyContent="space-between"
            >
              <Text size="xs">Asset</Text>
              <Text size="xs">Balance</Text>
            </Flex>
            <Flex px={4} py={3} justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <HederaLogo />
                <Text size="sm" variant="bodyBold" color="blackAlpha.600">
                  HBAR
                </Text>
              </Box>
              <Box flexShrink={0} ml={2} textAlign="right">
                <Skeleton isLoaded={!!currentAccount.balances} borderRadius={6} minH="20px">
                  <AnimatedNumber
                    value={Number(currentAccount.balances?.hbars?.replace(/[^0-9.]/g, '') || '0')}
                    animate={balanceCanAnimate}
                    size="sm"
                    variant="bodyBold"
                    color="blackAlpha.600"
                  />
                </Skeleton>
              </Box>
            </Flex>
            {topTokens.map((token) => (
              <Flex
                key={token.token_id}
                px={4}
                py={3}
                justifyContent="space-between"
                alignItems="center"
                cursor="pointer"
                borderRadius={8}
                _hover={{ bg: 'gray.50' }}
                transition="background 0.15s"
                onClick={() => {
                  setSelectedToken(token);
                  openModal(ModalType.TokenDetailsModal);
                }}
              >
                <Box display="flex" alignItems="center" gap={2} minW={0}>
                  <TokenIcon symbol={token.symbol} size={24} tokenId={token.token_id} />
                  <Box minW={0}>
                    <Text size="sm" variant="bodyBold" color="blackAlpha.700" noOfLines={1}>
                      {token.symbol}
                    </Text>
                    <Text fontSize="11px" color="blackAlpha.500" noOfLines={1}>
                      {token.name}
                    </Text>
                  </Box>
                </Box>
                <Box flexShrink={0} ml={2} textAlign="right">
                  <Text size="sm" variant="bodyBold" color="blackAlpha.600">
                    {formatTokenBalance(token)}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Box>
        </Flex>
        <TransactionList />
      </Flex>
    </Flex>
  );
};

export default AccountPage;
